import { Box } from '@mui/material';
import UPButton from 'components/button';
import { UPTanStackTable, UPTanStackTableColumn } from 'components/tanstack-table';
import { LinkType } from 'constants/LinkType';
import { toNumber } from 'lodash';
import { SectionContainer } from 'modules/companies/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import {
  deleteCompanyEmployeeInvitationLinks,
  getCompanyEmployeeInvitationLinks,
} from 'store/actions/employee-actions';
import { deleteJobApplicantLink, getJobApplicantLinks } from 'store/actions/job-applicant-link-actions';
import { employeeCompanyEmployeeInvitationLinksSelector } from 'store/selectors/employee-selector';
import { jobApplicantLinksSelector } from 'store/selectors/job-applicant-link-selectors';
import {
  COMPANY__LOCATIONS,
  DATAGRID__NO_ROWS,
  EMPLOYEES__IS_FOR_ALL_LOCATIONS,
  GENERAL__CANCEL_BUTTON,
  GENERAL__COPIED_TO_CLIPBOARD,
  GENERAL__TYPE,
} from 'translations/constants';
import { ReactComponent as CopyToClipboard } from '../../../../assets/icons/CopyToClipboard.svg';
import { ReactComponent as DeleteIconGreen } from '../../../../assets/icons/DeleteGreen.svg';
import { ModalFooter } from '../styles';
import { ICompanyLink, IGeneralInvitationProps } from '../types';
import LinkTypeBadge, { getLinkTypeLabel } from './LinkTypeBadge';
import mapCompanyLinks from './mapCompanyLinks';
import measureBadgeColumnWidth from './measureBadgeColumnWidth';
import { RowActionButton, RowActionsContainer } from './styles';

interface IActiveLinksTabProps extends IGeneralInvitationProps {
  /** When false only onboarding links are fetched and shown, exactly as before. */
  jobApplicantsEnabled: boolean;
}

const ActiveLinksTab = (props: IActiveLinksTabProps) => {
  const { companyId, onClose, jobApplicantsEnabled } = props;
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const [copied, setCopied] = useState(false);
  const [copiedRow, setCopiedRow] = useState<string>('');

  const [rowOnHover, setRowOnHover] = useState<string>();
  const invitations = useAppSelector(employeeCompanyEmployeeInvitationLinksSelector);
  const applicationLinks = useAppSelector(jobApplicantLinksSelector);

  useEffect(() => {
    if (companyId) dispatch(getCompanyEmployeeInvitationLinks(toNumber(companyId)));
  }, [companyId, dispatch]);

  useEffect(() => {
    if (companyId && jobApplicantsEnabled) dispatch(getJobApplicantLinks(toNumber(companyId)));
  }, [companyId, dispatch, jobApplicantsEnabled]);

  const rows: ICompanyLink[] = useMemo(
    () =>
      mapCompanyLinks(invitations, jobApplicantsEnabled ? applicationLinks : [], t(EMPLOYEES__IS_FOR_ALL_LOCATIONS)),
    [invitations, applicationLinks, jobApplicantsEnabled, t],
  );

  // Sized to the widest translated badge, so the column takes exactly the width it needs.
  const linkTypeColumnWidth = useMemo(
    () =>
      measureBadgeColumnWidth([getLinkTypeLabel(LinkType.Onboarding, t), getLinkTypeLabel(LinkType.Application, t)]),
    [t],
  );

  const handleCopyClick = useCallback((text: string, rowId: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setCopiedRow(rowId);
  }, []);

  // Both delete thunks refetch their own list internally, so the caller must not dispatch a
  // second get — that was a duplicate request racing itself.
  const onDeleteLink = useCallback(
    (row: ICompanyLink) => {
      if (row.linkType === LinkType.Application) {
        dispatch(deleteJobApplicantLink({ companyId: toNumber(companyId), id: row.id }));
        return;
      }
      dispatch(deleteCompanyEmployeeInvitationLinks({ id: row.id, companyId: toNumber(companyId) }));
    },
    [companyId, dispatch],
  );

  const linkTypeCellRenderer = useCallback(
    (info: { row: ICompanyLink }) => <LinkTypeBadge linkType={info.row.linkType} />,
    [],
  );

  const actionsCellRenderer = useCallback(
    (info: { row: ICompanyLink }) => {
      if (rowOnHover !== info.row.rowId) return null;
      return (
        <RowActionsContainer>
          {/* Listing is at-least-one-branch but deleting is all-of-them, so a visible row
              is not necessarily deletable. Hide the action instead of surfacing a 403. */}
          {info.row.canDelete && (
            <RowActionButton onClick={() => onDeleteLink(info.row)}>
              <DeleteIconGreen style={{ cursor: 'pointer' }} />
            </RowActionButton>
          )}
          <RowActionButton>
            <CopyToClipboard
              onClick={() => handleCopyClick(info.row.shareUrl, info.row.rowId)}
              style={{ cursor: 'pointer' }}
            />
            {copied && rowOnHover === copiedRow && (
              <span style={{ paddingLeft: '0.5rem', fontSize: '75%', whiteSpace: 'nowrap' }}>
                {t(GENERAL__COPIED_TO_CLIPBOARD)}
              </span>
            )}
          </RowActionButton>
        </RowActionsContainer>
      );
    },
    [copied, copiedRow, handleCopyClick, onDeleteLink, rowOnHover, t],
  );

  const columns: UPTanStackTableColumn<ICompanyLink>[] = useMemo(
    () => [
      {
        id: 'branchesLabel',
        accessorKey: 'branchesLabel',
        align: 'left',
        header: t(COMPANY__LOCATIONS),
        // Sized well above the other columns so it absorbs the leftover width, letting the
        // Type column stay at its content width without pushing the table into scroll.
        size: 400,
      },
      // Type sits to the right of Branches, so the branch list keeps the leading column and the
      // badge reads as a trailing attribute rather than the row's primary identity.
      //
      // Shown regardless of the feature flag: without it every row simply reads "Onboarding",
      // and keeping the column set stable means enabling the feature adds rows rather than
      // relaying out the table.
      {
        id: 'linkType',
        accessorKey: 'linkType',
        // Pill and header share the column's left edge. Centring the pill while the header
        // stayed left read as a misalignment, since the column is wider than the pill.
        align: 'left',
        headerAlign: 'left',
        size: linkTypeColumnWidth,
        header: t(GENERAL__TYPE),
        cell: linkTypeCellRenderer,
      },
      {
        id: 'actions',
        header: '',
        align: 'right',
        size: 150,
        enableSorting: false,
        cell: actionsCellRenderer,
      },
    ],
    [actionsCellRenderer, linkTypeCellRenderer, linkTypeColumnWidth, t],
  );

  return (
    <SectionContainer>
      {/* Capped rather than a plain 50vh: the modal is bounded at min(650px, 95vh), and a
          taller box pushed the pagination bar and the footer past that bound, so the modal
          scrolled vertically around a table that was already scrolling on its own. */}
      <Box sx={{ height: 'min(50vh, 360px)', width: '100%' }}>
        <UPTanStackTable<ICompanyLink>
          data={rows}
          columns={columns}
          getRowId={row => row.rowId}
          emptyMessage={t(DATAGRID__NO_ROWS)}
          // Row ids are composite strings (`<linkType>-<id>`), so the numeric `setRowOnHover`
          // callback would coerce them to NaN — the string-preserving hook is required here.
          onRowHover={rowId => setRowOnHover(rowId === null ? undefined : String(rowId))}
        />
      </Box>
      <ModalFooter>
        <Box sx={{ marginLeft: '12px' }}>
          <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={onClose} outlined />
        </Box>
      </ModalFooter>
    </SectionContainer>
  );
};

export default ActiveLinksTab;
